import React, { useState, createContext, useCallback, useEffect } from "react";
import authApi from "../../api/authApi";

export const AuthContext = createContext();

const AuthContextProvider = ({ children }) => {
  const [state, setState] = useState({
    token: localStorage.getItem("token") || null,
    username: localStorage.getItem("username") || null,
    userType: localStorage.getItem("user_type") || null,
    clientId: localStorage.getItem("client_id") || null,
    storeId: localStorage.getItem("store_id") || null,
    storeName: localStorage.getItem("emailApp_storeName") || null,
    isLoading: false,
    errorMsg: "",
    successMsg: ""
  });
  const [clients, setClients] = useState([]);

  const updateState = useCallback((updates) => {
    setState((prev) => ({ ...prev, ...updates }));
  }, []);

  const switchStore = useCallback((storeId, storeName, shouldReload = true) => {
    localStorage.setItem("store_id", storeId);
    if (storeName) {
      localStorage.setItem("emailApp_storeName", storeName);
    } else {
      localStorage.removeItem("emailApp_storeName");
    }
    updateState({ storeId, storeName: storeName || null });
    if (shouldReload) {
      window.location.reload();
    }
  }, [updateState]);

  const switchClient = useCallback((clientId, clientList, shouldReload = true) => {
    localStorage.setItem("client_id", clientId);
    updateState({ clientId });

    const listToSearch = clientList || clients;
    const client = listToSearch?.find(c => c.id === parseInt(clientId));
    const stores = client?.stores || [];

    if (stores.length === 1) {
      switchStore(stores[0].id, stores[0].name, false);
    } else {
      localStorage.removeItem("store_id");
      localStorage.removeItem("emailApp_storeName");
      updateState({ storeId: null, storeName: null });
    }
    if (shouldReload) {
      window.location.reload();
    }
  }, [updateState, switchStore, clients]);

  const signinUser = useCallback(async (username, password) => {
    updateState({ isLoading: true, errorMsg: "" });
    try {
      const data = await authApi.login(username, password);

      const { token, user, clients: userClients } = data;
      localStorage.setItem("token", token);
      localStorage.setItem("username", user.email);
      localStorage.setItem("user_type", user.user_type);
      setClients(userClients || []);

      let defaultClientId = null;
      let defaultStoreId = null;
      let defaultStoreName = "";

      if (userClients && userClients.length > 0) {
        if (userClients.length === 1 || user.user_type !== "staff") {
          const client = userClients[0];
          defaultClientId = client.id;
          localStorage.setItem("client_id", client.id);

          const stores = client.stores || [];
          if (stores.length === 1) {
            const store = stores[0];
            defaultStoreId = store.id;
            defaultStoreName = store.name;
            localStorage.setItem("store_id", store.id);
            localStorage.setItem("emailApp_storeName", store.name);
          }
        }
      }

      updateState({
        token: token,
        username: user.email,
        userType: user.user_type,
        clientId: defaultClientId,
        storeId: defaultStoreId,
        storeName: defaultStoreName,
        isLoading: false,
        successMsg: "Login successful!"
      });
    } catch (err) {
      updateState({
        isLoading: false,
        errorMsg: err.message || "Failed to sign in. Please verify backend is running."
      });
    }
  }, [updateState]);

  const signoutUser = useCallback(() => {
    localStorage.removeItem("token");
    localStorage.removeItem("username");
    localStorage.removeItem("user_type");
    localStorage.removeItem("client_id");
    localStorage.removeItem("store_id");
    localStorage.removeItem("emailApp_storeName");
    setClients([]);
    updateState({
      token: null,
      username: null,
      userType: null,
      clientId: null,
      storeId: null,
      storeName: null,
      errorMsg: "",
      successMsg: ""
    });
  }, [updateState]);

  useEffect(() => {
    if (state.token) {
      authApi.getMe()
      .then(data => {
        const { user, clients: userClients } = data;
        setClients(userClients || []);
        
        localStorage.setItem("user_type", user.user_type);
        updateState({
          username: user.email,
          userType: user.user_type,
        });

        let activeClientId = localStorage.getItem("client_id") || null;
        let activeStoreId = localStorage.getItem("store_id") || null;
        let activeStoreName = localStorage.getItem("emailApp_storeName") || null;

        if (userClients && userClients.length > 0) {
          if (!activeClientId) {
            if (userClients.length === 1 || user.user_type !== "staff") {
              const client = userClients[0];
              activeClientId = client.id;
              localStorage.setItem("client_id", client.id);
            }
          }

          if (activeClientId) {
            const client = userClients.find(c => c.id === parseInt(activeClientId));
            const stores = client?.stores || [];
            if (!activeStoreId && stores.length === 1) {
              const store = stores[0];
              activeStoreId = store.id;
              activeStoreName = store.name;
              localStorage.setItem("store_id", store.id);
              localStorage.setItem("emailApp_storeName", store.name);
            }
          }
        }

        updateState({
          clientId: activeClientId,
          storeId: activeStoreId,
          storeName: activeStoreName
        });
      })
      .catch(err => {
        if (err.status === 401) {
          signoutUser();
        }
        console.error("Error fetching self profile", err);
      });
    }
  }, [state.token, updateState, signoutUser]);

  const contextValue = {
    ...state,
    clients,
    setClients,
    signin: signinUser,
    signout: signoutUser,
    switchClient,
    switchStore,
    handleUpdate: updateState
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContextProvider;
export { AuthContextProvider };
